import React, { useState } from "react";
import { IdCard, Globe, Car, CreditCard } from "lucide-react";
import Toast from "../components/Toast";
import Lottie from "lottie-react";
import successAnimation from "../assets/lottie/success.json";
import axios from 'axios';
import {API_BASE_URL} from "../config/constants";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content'


export default function CheckInVerificationModal({ show, onClose }) {
  const [step, setStep] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [formData, setFormData] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [verifiedUser, setVerifiedUser] = useState(null);

  const methods = [
    { name: "NIN", icon: <IdCard size={28} />, description: "Verify using National Identification Number." },
    { name: "Passport", icon: <Globe size={28} />, description: "Verify using international passport details." },
    { name: "Driver’s License", icon: <Car size={28} />, description: "Verify using driver’s license number." },
    { name: "BVN", icon: <CreditCard size={28} />, description: "Verify using Bank Verification Number." },
    { name: "VotersCard", icon: <CreditCard size={28} />, description: "Verify using the Voter’s Card Number and DOB." },
  ];

  const resetModal = () => {
    setStep(1);
    setSelectedMethod(null);
    setFormData({});
    setVerifiedUser(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
    location.reload();
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleVerification = async () => {
    try {
      let response;

      withReactContent(Swal).fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Verifying your guest...',
        showConfirmButton: false,
      });

      // Make different API calls based on selected method
      switch (selectedMethod) {
        case 'NIN':
          response = await axios.post(`${API_BASE_URL}/verification/nin`, {
            nin: formData.nin
          });
          break;

        case 'Passport':
          response = await axios.post(`${API_BASE_URL}/verification/passport`, {
            number: formData.passport,
            dob: formData.dob,
            lastName: formData.lastName
          });
          break;

        case 'Driver’s License':
          response = await axios.post(`${API_BASE_URL}/verification/drivers-license`, {
            licenseNumber: formData.license
          });
          break;

        case 'BVN':
          response = await axios.post(`${API_BASE_URL}/verification/bvn`, {
            bvn: formData.bvn
          });
          break;

        case 'VotersCard':
          response = await axios.post(`${API_BASE_URL}/verification/voters-card`, {
            cardNumber: formData.voterCard,
            dateOfBirth: formData.dob
          });
          break;

        default:
          throw new Error('Invalid verification method');
      }

      // Handle successful verification
      if (response.data.status === "Success") {
        withReactContent(Swal).close();
        const userData = {};
        if (selectedMethod === "NIN") {
          userData.currentFirstName = response.data.data.firstname
          userData.currentLastName = response.data.data.surname
          userData.currentGender = response.data.data.gender
          userData.currentDateOfBirthLabel = response.data.data.birthdate
          userData.image = `data:image/png;base64,${response.data.data.photo}`
          userData.method = "NIN"
        } else if (selectedMethod === "Passport") {
          userData.currentFirstName = response.data.data.currentFirstName
          userData.currentLastName = response.data.data.currentLastName
          userData.currentGender = response.data.data.currentGender
          userData.currentDateOfBirthLabel = response.data.data.currentDateOfBirthLabel
          userData.image = null
          userData.method = "Passport"
        }

        setVerifiedUser({
          name: `${userData.currentFirstName} ${userData.currentLastName}`,
          gender: userData.currentGender,
          img: userData.image, // You might want to get this from the API too
          method: selectedMethod,
          verifiedAt: new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          // Add any additional fields you want to display
          additionalDetails: {
            dateOfBirth: userData.currentDateOfBirthLabel,
            passportNumber: userData.currentPassportNumber,
            // etc...
          }
        });
        showToast(`Guest verified via ${selectedMethod}!`, "success");
        const storageData = {
          name: `${userData.currentFirstName} ${userData.currentLastName}`,
          date: new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          method: userData.method,
          status: "Verified",
          img: userData.image
        }
        addGuest(storageData);
        setStep(3);
      } else {
        withReactContent(Swal).close();
        showToast(response.data.detail || "Verification failed", "error");
      }
    } catch (error) {
      console.error("Verification error:", error);
      showToast(error.response?.data?.message || "Verification failed", "error");
    }
  };

  const addGuest = (newGuest) => {
    const updatedGuests = [newGuest];
    localStorage.setItem("newGuest", JSON.stringify(updatedGuests));
  };

  /*const handleVerification = () => {
    setTimeout(() => {
      setVerifiedUser({
        name: "John Doe",
        gender: "Male",
        img: userImage,
        method: selectedMethod,
        verifiedAt: new Date().toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
      });
      showToast(`Guest verified via ${selectedMethod}!`, "success");
      setStep(3);
    }, 1000);
  };*/

  // Input vertical padding style
  const inputStyle = { paddingTop: "12px", paddingBottom: "12px" };

  return (
    <>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false })} />

      <div className={`modal ${show ? "d-block show" : ""}`} tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content rounded-3 shadow">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Check-In Verification</h5>
              <button type="button" className="btn-close" onClick={handleClose}></button>
            </div>

            {/* Body */}
            <div className="modal-body px-5">
              {/* Step 1: Select Method */}
              {step === 1 && (
                <>
                  <h4 className="text-center fw-bold mb-3">Select Verification Method</h4>
                  <div className="row g-3">
                    {methods.map((method) => (
                      <div className="col-12 col-md-6" key={method.name}>
                        <div
                          className={`card border rounded-3 p-3 h-100 ${
                            selectedMethod === method.name ? "border-primary shadow-sm" : "border-secondary"
                          }`}
                          style={{ transition: "0.3s", cursor: "pointer" }}
                          onClick={() => setSelectedMethod(method.name)}
                        >
                          <div className="text-center">
                            <div className={`mb-2 ${selectedMethod === method.name ? "text-primary" : "text-muted"}`}>
                              {method.icon}
                            </div>
                            <h5 className="fw-bold">{method.name}</h5>
                            <p className="small text-muted">{method.description}</p>
                            <button
                              className={`btn btn-sm ${
                                selectedMethod === method.name ? "btn-primary" : "btn-outline-primary"
                              } mt-2`}
                            >
                              Select {method.name}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="text-end mt-3">
                    <button className="btn btn-primary" disabled={!selectedMethod} onClick={() => setStep(2)}>
                      Continue →
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Input Fields */}
              {step === 2 && (
                <div>
                  <h6 className="fw-bold mb-3">Enter {selectedMethod} Details</h6>

                  {/* NIN */}
                  {selectedMethod === "NIN" && (
                    <div className="mb-3">
                      <label htmlFor="nin" className="form-label fw-semibold">
                        National Identification Number (NIN)
                      </label>
                      <input
                        id="nin"
                        type="text"
                        className="form-control"
                        placeholder="Enter NIN"
                        style={inputStyle}
                        onChange={(e) => handleInputChange("nin", e.target.value)}
                      />
                    </div>
                  )}

                  {/* Passport */}
                  {selectedMethod === "Passport" && (
                    <>
                      <div className="mb-3">
                        <label htmlFor="passport" className="form-label fw-semibold">
                          Passport Number
                        </label>
                        <input
                          id="passport"
                          type="text"
                          className="form-control"
                          placeholder="Enter Passport Number"
                          style={inputStyle}
                          onChange={(e) => handleInputChange("passport", e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="last-name" className="form-label fw-semibold">
                          Last Name
                        </label>
                        <input
                          id="last-name"
                          type="text"
                          className="form-control"
                          placeholder="Enter Last Name"
                          style={inputStyle}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="dobPassport" className="form-label fw-semibold">
                          Date of Birth
                        </label>
                        <input
                          id="dobPassport"
                          type="date"
                          className="form-control"
                          style={inputStyle}
                          onChange={(e) => handleInputChange("dob", e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  {/* Driver’s License */}
                  {selectedMethod === "Driver’s License" && (
                    <div className="mb-3">
                      <label htmlFor="license" className="form-label fw-semibold">
                        Driver’s License Number
                      </label>
                      <input
                        id="license"
                        type="text"
                        className="form-control"
                        placeholder="Enter Driver’s License Number"
                        style={inputStyle}
                        onChange={(e) => handleInputChange("license", e.target.value)}
                      />
                    </div>
                  )}

                  {/* BVN */}
                  {selectedMethod === "BVN" && (
                    <div className="mb-3">
                      <label htmlFor="bvn" className="form-label fw-semibold">
                        Bank Verification Number (BVN)
                      </label>
                      <input
                        id="bvn"
                        type="text"
                        className="form-control"
                        placeholder="Enter BVN"
                        style={inputStyle}
                        onChange={(e) => handleInputChange("bvn", e.target.value)}
                      />
                    </div>
                  )}

                  {/* VotersCard */}
                  {selectedMethod === "VotersCard" && (
                    <>
                      <div className="mb-3">
                        <label htmlFor="voterCard" className="form-label fw-semibold">
                          Voter’s Card Number
                        </label>
                        <input
                          id="voterCard"
                          type="text"
                          className="form-control"
                          placeholder="Enter Voter’s Card Number"
                          style={inputStyle}
                          onChange={(e) => handleInputChange("voterCard", e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="dobVoter" className="form-label fw-semibold">
                          Date of Birth
                        </label>
                        <input
                          id="dobVoter"
                          type="date"
                          className="form-control"
                          style={inputStyle}
                          onChange={(e) => handleInputChange("dob", e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  <div className="d-flex justify-content-between">
                    <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
                      ← Back
                    </button>
                    <button className="btn btn-primary" onClick={handleVerification}>
                      Lookup
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Success Card */}
              {step === 3 && verifiedUser && (
                <div className="text-center">
                  <Lottie animationData={successAnimation} loop={false} style={{ height: 120, marginBottom: 15 }} />

                  {/* Improved User Card */}
                  <div
                    className="card mx-auto p-4"
                    style={{
                      maxWidth: 320,
                      borderRadius: "12px",
                      border: "1px solid #e0e0e0",
                      backgroundColor: "white",
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      { verifiedUser.img && <div
                        className="rounded overflow-hidden mb-3"
                        style={{ width: 120, height: 120, background: "#f8f9fa" }}
                      >
                         <img
                          src={verifiedUser.img}
                          alt={verifiedUser.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                      }
                      <h5 className="fw-bold mb-1">{verifiedUser.name}</h5>
                      <p className="mb-1 text-secondary" style={{ fontSize: "0.9rem" }}>
                        Gender: {verifiedUser.gender.toUpperCase()}
                      </p>
                      <p className="mb-1 text-secondary" style={{ fontSize: "0.9rem" }}>
                        Verified via: <strong>{verifiedUser.method}</strong>
                      </p>
                      <p className="mb-3 text-secondary" style={{ fontSize: "0.8rem" }}>
                        Verified on: {verifiedUser.verifiedAt}
                      </p>
                    </div>
                  </div>

                  {/* Success text below card */}
                  <div className="mt-4">
                    <h3 className="fw-bold cool-font">Verification Successful</h3>
                    <p className="text-muted" style={{ maxWidth: 400, margin: "auto", fontSize: "1rem" }}>
                      The guest's identity has been successfully verified and recorded. You can now proceed with
                      the check-in process confidently.
                    </p>
                  </div>

                  <div className="d-flex justify-content-between mt-4">
                    <button className="btn btn-outline-secondary" onClick={() => setStep(2)}>
                      ← Back
                    </button>
                    <button className="btn btn-success" onClick={handleClose}>
                      Finish & Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
